"use client";

import * as React from "react";
import { Gift, X } from "lucide-react";

import { cn } from "@/lib/utils";
import styles from "./productCardHorizontal.module.css";

/**
 * Horizontal product row — the typical cart line item: thumbnail (image
 * URL, Gift-icon tile fallback), title + subtitle, price text with an
 * optional strikeout beside it, and an optional remove button. Pure
 * display props (the gift cart feeds it from the cookie snapshot — see
 * src/lib/giftCart.ts); no CMS coupling.
 */
export interface ProductCardHorizontalProps {
  title: string;
  subtitle?: string;
  /** Price text as it should display, e.g. "$72". */
  priceText?: string;
  strikeoutPrice?: string;
  imageUrl?: string | null;
  /** Renders the remove ("X") button when provided. */
  onRemove?: () => void;
}

const ProductCardHorizontal = ({
  title,
  subtitle,
  priceText,
  strikeoutPrice,
  imageUrl,
  onRemove,
}: ProductCardHorizontalProps) => {
  return (
    <div className={styles.root}>
      <div className={styles.thumb}>
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element -- small fixed-size thumb; next/image is overkill for a 56px tile
          <img className={styles.thumbImage} src={imageUrl} alt="" />
        ) : (
          <Gift className={styles.thumbIcon} strokeWidth={1.5} aria-hidden />
        )}
      </div>

      <div className={styles.details}>
        <p className={cn("title-xs", styles.title)}>{title}</p>
        {subtitle && <p className={cn("body-xs", "muted", styles.subtitle)}>{subtitle}</p>}
      </div>

      <div className={styles.priceBlock}>
        {priceText && <p className={cn("accent-sm", styles.price)}>{priceText}</p>}
        {strikeoutPrice && (
          <p className={cn("body-xs", styles.strikeout)}>{strikeoutPrice}</p>
        )}
      </div>

      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          aria-label={`Remove ${title} from cart`}
          onClick={onRemove}
        >
          <X width={16} height={16} strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  );
};

export default ProductCardHorizontal;
